import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { motion } from "framer-motion";
import { data } from "../../Components/OurProducts/contents";
import diagram from "../../Assets/diagram.png";
import questions from "../../Assets/products/questions.jpg";
import KEYWORDS from "../../Constants/keywords";


import {
  H2,
  ParagraphLg,
  SubHeading,
  Divider,
} from "../../Constants/typography";
import { COLORS } from "../../Constants/colors";

// ============ Styled Components ============

const Section = styled.div`
  padding: 30px 80px;
  box-sizing: border-box;
  background-color: ${COLORS.background};

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  margin-bottom: 96px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const Card = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${COLORS.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 20px ${COLORS.shadowColor};
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  min-height: 300px;
  background-color: ${COLORS.imageBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  width: 100%;
  max-width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: center;
  }
`;

const Heading = styled.h3`
  font-size: 1rem;
  color: ${COLORS.primary};
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PainList = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  list-style: none;
  padding: 0;
  margin: 0;

  @media (min-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PainItem = styled.li`
  background-color: ${COLORS.background};
  color: ${COLORS.textPrimary};
  padding: 1rem 1.25rem;
  border-left: 4px solid ${COLORS.primary};
  border-radius: 10px;
  transition: 0.3s;
  font-size: 1.4rem;

  &:hover {
    background-color: ${COLORS.cardHoverBackground};
    color: ${COLORS.primary};
    transform: translateX(5px);
  }
`;

const Diagram = styled.img`
  width: 800px;
  height: 500px;
  margin: 48px auto;
  display: block;     
  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`;

const ProductWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  align-items: center;
  justify-content: space-between;
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Col6 = styled.div`
  flex: 1;
  min-width: 300px;
  @media (max-width: 1024px) {
    order: 2; /* Content comes second */
    width: 100%;
    text-align: center;
  }
`;

const ProductImageWrapper = styled.div`
  flex: 1;
  min-width: 280px;
  display: flex;
  justify-content: center;
  @media (max-width: 1024px) {
    order: 1; /* Image comes first */
    width: 100%;
  }

`;

const SVGContainer = styled.div`
  width: 100%;
  max-width: 480px;
  height: auto;

  svg {
    width: 100%;
    height: auto;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: auto;
    object-fit: fit;
    border-radius: 12px;
    box-shadow: 0 4px 20px ${COLORS.shadowColor};
  }

  @media (max-width: 1024px) {
    max-width: 100%;
  }
  @media (max-width: 2024px) {
    img {
      min-height: 400px;
      max-height: 500px;
    }
  }
`;

// ============ Components ============

const PainPointsContent = () => (
  <Card>
    <ImageContainer>
      <Image src={questions} alt="SmartSync Pain Points"/>
    </ImageContainer>
    <Content>
      <Heading>Are You Struggling With</Heading>
      <PainList>
        <PainItem>Business with distributed data across locations</PainItem>
        <PainItem>Difficult to collate your business data for analysis</PainItem>
        <PainItem>New business potential if you get your data online</PainItem>
        <PainItem>
          Lacking business intelligence, real-time dashboards and reports
        </PainItem>
        <PainItem>Planning to migrate to Newgen technologies</PainItem>
        <PainItem>Business data in DBF, CSV, Other sources</PainItem>
      </PainList>
    </Content>
  </Card>
);

const OurProductsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Helmet>
        <meta name="keywords" content={KEYWORDS.smartsync.join(", ")} />
      </Helmet>
      <Section>
        <SubHeading>PRODUCTS</SubHeading>
        <Divider />
        <PainPointsContent />

        <Diagram src={diagram} alt="Data Diagram"/>

        <div style={{ marginTop: "48px" }}>
          {data.map((item, index) => {
            const image = item.image;
            const isEven = index % 2 === 0;

            const ProductImage = (
              <ImageWrapper>
                <img src={image} alt={item.subheading}/>
              </ImageWrapper>
            );

            return (
              <ProductWrapper key={item.id} style={{ marginBottom: "64px" }}>
                {isEven ? (
                  <>
                    <Col6>
                      <H2>SmartSync</H2>
                      <SubHeading as="div">
                        <strong>{item.subheading}</strong>
                      </SubHeading>
                      <ParagraphLg>{item.paragrapgh}</ParagraphLg>
                    </Col6>
                    <ProductImageWrapper>{ProductImage}</ProductImageWrapper>
                  </>
                ) : (
                  <>
                    <ProductImageWrapper>{ProductImage}</ProductImageWrapper>
                    <Col6>
                      <H2>SmartSync</H2>
                      <SubHeading as="div">
                        <strong>{item.subheading}</strong>
                      </SubHeading>
                      <ParagraphLg>{item.paragrapgh}</ParagraphLg>
                    </Col6>
                  </>
                )}
              </ProductWrapper>
            );
          })}
        </div>
      </Section>
    </>
  );
};

export default OurProductsPage;


